import {
  comparePrimarySubtextsSearchMatch,
  getPrimarySubtextsSortLabel,
  scorePrimarySubtextsForItem,
  sortMetaPrimarySubtextsForItem,
  sortSectionedRowsByLeafSearchMatch,
} from '../../utils';

import type { MenuListItemData, MenuListOptionItemData, MenuListSectionItemData } from './types';

const getMenuListPrimarySearchText = (item: MenuListOptionItemData): string => {
  const textIsStringOrNumber = typeof item.text === 'string' || typeof item.text === 'number';
  return textIsStringOrNumber ? String(item.text) : '';
};

const menuListLeafSubs = (item: MenuListOptionItemData) => item.subTexts;

/**
 * `text`를 이름처럼 0·1·2단계로 매칭하고, 실패 시 `subTexts`를 카테고리처럼 3·4단계로 매칭한다.
 */
export const getMenuListOptionSearchMatchScore = (
  item: MenuListOptionItemData,
  searchKeyword: string,
): number =>
  scorePrimarySubtextsForItem(item, searchKeyword, getMenuListPrimarySearchText, menuListLeafSubs);

/**
 * `searchText`가 비어 있지 않을 때 옵션이 매칭되는지 여부.
 */
export const menuListOptionMatchesSearchText = (
  item: MenuListOptionItemData,
  searchText: string,
): boolean => {
  if (!searchText.trim()) return true;
  return Number.isFinite(getMenuListOptionSearchMatchScore(item, searchText));
};

/**
 * 기본 필터 + 검색 우선순위 정렬. `filterItems` 커스텀이 없을 때 MenuList·네비게이션에서 공통 사용.
 */
export const filterAndSortMenuListItems = (
  items: readonly MenuListItemData[],
  searchText: string,
): MenuListItemData[] => {
  const kw = searchText.trim();
  if (!kw) return [...items];

  const filtered = items
    .map((item): MenuListItemData | null => {
      if (item.type === 'section') {
        const filteredSectionItems = item.items?.filter(child =>
          menuListOptionMatchesSearchText(child, searchText),
        );
        if (!filteredSectionItems?.length) return null;
        return { ...item, items: filteredSectionItems } as MenuListSectionItemData;
      }
      return menuListOptionMatchesSearchText(item, searchText) ? item : null;
    })
    .filter((item): item is MenuListItemData => item !== null);

  return sortFilteredMenuListItems(filtered, searchText);
};

const sortFilteredMenuListItems = (
  items: MenuListItemData[],
  searchKeyword: string,
): MenuListItemData[] =>
  sortSectionedRowsByLeafSearchMatch<MenuListItemData, MenuListOptionItemData>({
    rows: items,
    searchKeyword,
    preserveStableOrderWhenSearchMetaTies: true,
    isSection: (row): row is MenuListSectionItemData => row.type === 'section',
    getSectionLeaves: row => [...((row as MenuListSectionItemData).items ?? [])],
    cloneSectionWithLeaves: (section, sortedLeaves) => ({
      ...(section as MenuListSectionItemData),
      items: sortedLeaves,
    }),
    compareLeaves: (a, b, keyword) =>
      comparePrimarySubtextsSearchMatch(
        a,
        b,
        keyword,
        getMenuListPrimarySearchText,
        menuListLeafSubs,
        { preserveStableOrderWhenSearchMetaTies: true },
      ),
    getLeafSearchSortMeta: (leaf, keyword) =>
      sortMetaPrimarySubtextsForItem(leaf, keyword, getMenuListPrimarySearchText, menuListLeafSubs),
    getSectionSortLabel: section => String((section as MenuListSectionItemData).title ?? ''),
    getLeafSortLabel: leaf =>
      getPrimarySubtextsSortLabel(getMenuListPrimarySearchText(leaf), menuListLeafSubs(leaf)),
  });
